import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { REPORTS_BASE_URL, CATEGORY_SOLD_REPORT_API } from "../../../../Constants/Config";

const initialState = {
    loading: false,
    categorySalesData: [],
    headerTotals: null,
    topCategories: {
        byQuantity: null,
        bySales: null,
        byProfit: null,
        byMargin: null,
    },
    status: false,
    successMessage: "",
    error: "",
};

export const fetchCategorySalesData = createAsyncThunk(
    "CategorySalesReport/fetchCategorySalesData",
    async (data, { rejectWithValue }) => {
        try {
            const { token, ...dataNew } = data;

            const response = await axios.post(
                REPORTS_BASE_URL + CATEGORY_SOLD_REPORT_API,
                { ...dataNew, page: 1, page_size: 500 },
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.status === true) {
                // /reports/category_sales_report_api reads from the same
                // report_item_sales_daily snapshot (grouped by primary
                // category) that the item-level drill-down (items_sales_report_api,
                // cat_match_mode="exact") also reads from, so a category row's
                // totals reconcile with what its drill-down shows. The legacy
                // NewDashboardReact/category_sales_report_list endpoint this
                // used to call read live order_items with a different formula,
                // which is why the two views used to disagree. Map its field
                // names onto the ones the rest of this slice/component already
                // expect (qty_sold/sale_amount/etc.) so nothing downstream changes.
                const categoryData = (response.data.category_sale_data || []).map((cat) => ({
                    category_id: cat.category_id,
                    // EVERY `collection` id this row is built from - one per
                    // store that carries the category, since `collection` is
                    // merchant-scoped and the report groups stores together.
                    // This is what the drill-down sends; without it here the
                    // mapping silently reduced the row to its single
                    // category_id and the drill-down queried one store's half.
                    category_ids: Array.isArray(cat.category_ids) ? cat.category_ids : [],
                    category_name: cat.category_name,
                    qty_sold: cat.total_qty,
                    sale_amount: cat.subtotal,
                    costs: cat.cost,
                    discount_amt: cat.discounts,
                    net_sales: cat.subtotal_with_discounts,
                    gross_profit: cat.profit,
                }));

                // Calculate gross margin percentage for each category.
                // TWO decimals, not one: every consumer renders this with
                // .toFixed(2), so a one-decimal string was displayed as if it
                // were precise - a row whose own numbers give 51.657% printed
                // "51.70%" beside a Total Summary card (computed from the same
                // profit/net, unrounded) printing "51.66%" for the very same
                // single category.
                const enrichedData = categoryData.map(cat => ({
                    ...cat,
                    gross_margin_percent: cat.net_sales > 0
                        ? ((cat.gross_profit / cat.net_sales) * 100).toFixed(2)
                        : 0
                }));

                // Find top categories
                const topByQuantity = enrichedData.length > 0
                    ? enrichedData.reduce((max, cat) => cat.qty_sold > max.qty_sold ? cat : max)
                    : null;

                const topBySales = enrichedData.length > 0
                    ? enrichedData.reduce((max, cat) => cat.net_sales > max.net_sales ? cat : max)
                    : null;

                const topByProfit = enrichedData.length > 0
                    ? enrichedData.reduce((max, cat) => cat.gross_profit > max.gross_profit ? cat : max)
                    : null;

                const topByMargin = enrichedData.length > 0
                    ? enrichedData.reduce((max, cat) => parseFloat(max.gross_margin_percent) > parseFloat(cat.gross_margin_percent) ? max : cat)
                    : null;

                const hasHeaderTotals =
                    response.data.total_subtotal != null &&
                    response.data.total_subtotal_with_discounts != null;

                return {
                    category_data: enrichedData,
                    header_totals: hasHeaderTotals
                        ? {
                            grossSales: parseFloat(response.data.total_subtotal) || 0,
                            netSales: parseFloat(response.data.total_subtotal_with_discounts) || 0,
                            discountAmount: parseFloat(response.data.total_discounts) || 0,
                            loyaltyAmount: 0,
                            cost: parseFloat(response.data.total_cost) || 0,
                            grossProfit: parseFloat(response.data.total_profit) || 0,
                        }
                        : null,
                    top_categories: {
                        byQuantity: topByQuantity,
                        bySales: topBySales,
                        byProfit: topByProfit,
                        byMargin: topByMargin,
                    },
                    status: response.data.status
                };
            }

            return rejectWithValue({
                message: response.data.message || "Failed to fetch data",
                status: response.status,
                data: response.data
            });
        } catch (error) {
            const customError = {
                message: error.message,
                status: error.response ? error.response.status : "Network Error",
                data: error.response ? error.response.data : null,
            };
            return rejectWithValue(customError);
        }
    }
);

const CategorySalesReportSlice = createSlice({
    name: "CategorySalesReport",
    initialState,
    reducers: {
        clearCategorySalesData: (state) => {
            state.categorySalesData = [];
            state.headerTotals = null;
            state.topCategories = {
                byQuantity: null,
                bySales: null,
                byProfit: null,
                byMargin: null,
            };
            state.status = false;
            state.loading = false;
            state.error = "";
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchCategorySalesData.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchCategorySalesData.fulfilled, (state, action) => {
            state.loading = false;
            state.categorySalesData = action.payload.category_data || [];
            state.headerTotals = action.payload.header_totals || null;
            state.topCategories = action.payload.top_categories || {
                byQuantity: null,
                bySales: null,
                byProfit: null,
                byMargin: null,
            };
            state.status = action.payload.status || false;
            state.error = "";
        });
        builder.addCase(fetchCategorySalesData.rejected, (state, action) => {
            state.loading = false;
            state.categorySalesData = [];
            state.headerTotals = null;
            state.topCategories = {
                byQuantity: null,
                bySales: null,
                byProfit: null,
                byMargin: null,
            };
            state.status = false;
            state.error = action.error.message;
        });
    },
});

export const { clearCategorySalesData } = CategorySalesReportSlice.actions;
export default CategorySalesReportSlice.reducer;
